import { z } from 'zod';
import {
  AccountStatus,
  FeedbackCategory,
  KycStatus,
  NotificationCategory,
  VendorExperienceLevel,
  VendorTeamSize,
  VendorVerificationStatus,
} from './user.enums';
import { EventCategory } from '../event';
import { PublicVendorListCategorySchema } from '../marketplace';

export const HostProfileResponseSchema = z.object({
  id: z.uuid(),
  userId: z.uuid(),
  businessName: z.string(),
  totalEvents: z.number(),
  totalSpent: z.number(),
  averageRating: z.number(),
  /** Count of vendor→host marketplace reviews reflected in `averageRating`. */
  totalReviews: z.number().int().nonnegative(),
  defaultCurrency: z.string(),
  description: z.string().optional(),
  category: z.array(z.enum(EventCategory)),
  logoUrl: z.url().optional(),
  hasPin: z.boolean(),
  businessRegNumber: z.string().optional(),
  taxId: z.string().optional(),
  settlementBankCode: z.string().optional(),
  settlementAccountNumber: z.string().optional(),
  settlementAccountName: z.string().optional(),
  paystackSubaccountCode: z.string().optional(),
  createdAt: z.iso.datetime(),
});

export const VendorProfileResponseSchema = z.object({
  id: z.uuid(),
  userId: z.uuid(),
  companyName: z.string(),
  bio: z.string(),
  yearsExperience: z.number().optional(),
  teamSize: z.enum(VendorTeamSize).optional(),
  experienceLevel: z.enum(VendorExperienceLevel),
  equipmentManifest: z.array(z.record(z.string(), z.any())),
  serviceCategories: z.array(PublicVendorListCategorySchema),
  equipmentItems: z.array(PublicVendorListCategorySchema),
  verificationStatus: z.enum(VendorVerificationStatus),
  averageRating: z.number().positive().multipleOf(0.01),
  totalReviews: z.number(),
  completionRate: z.number(),
  isAvailable: z.boolean(),
  hasPin: z.boolean(),
  createdAt: z.iso.datetime(),
});

export const UserResponseSchema = z.object({
  id: z.uuid(),
  email: z.email().toLowerCase(),
  emailVerified: z.boolean(),
  emailVerifiedAt: z.iso.datetime().optional(),
  phoneNumber: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  displayName: z.string().optional(),
  avatarUrl: z.url().optional(),
  roles: z.number().min(1).max(15),
  accountStatus: z.enum(AccountStatus),
  kycStatus: z.enum(KycStatus),
  hostProfile: HostProfileResponseSchema.optional(),
  vendorProfile: VendorProfileResponseSchema.optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  eventCategory: z.array(z.enum(EventCategory)).optional(),
});

export const UpdateUserRequestSchema = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  displayName: z.string().optional(),
  avatarUrl: z.url().optional(),
  phoneNumber: z.string().optional(),
  eventCategory: z.array(z.enum(EventCategory)).optional(),
});

export const CreateHostProfileRequestSchema = z.object({
  businessName: z.string(),
  description: z.string().optional(),
  logoUrl: z.string().optional(),
  category: z.array(z.enum(EventCategory)).min(1),
});

export const UpdateHostProfileRequestSchema = z.object({
  businessName: z.string().min(1).max(120).optional(),
  description: z.string().max(2000).optional(),
  logoUrl: z.string().optional(),
  category: z.array(z.enum(EventCategory)).min(1).optional(),
  businessRegNumber: z.string().max(64).optional(),
  taxId: z.string().max(64).optional(),
});

export const CreateVendorProfileRequestSchema = z.object({
  companyName: z.string(),
  bio: z.string(),
  logoUrl: z.string().optional(),
  serviceCategoryIds: z.array(z.uuid()).optional(),
  serviceCategoryCodes: z.array(z.string().min(1).max(64)).optional(),
  equipmentItemIds: z.array(z.uuid()).optional(),
  equipmentItemCodes: z.array(z.string().min(1).max(64)).optional(),
  yearsExperience: z.number(),
  experienceLevel: z.enum(VendorExperienceLevel),
  teamSize: z.enum(VendorTeamSize).optional(),
  minFee: z.number(),
  maxFee: z.number(),
  isAvailable: z.boolean(),
});

export const SetProfilePinSchema = z.object({
  pin: z
    .string()
    .trim()
    .regex(/^\d{5}$/, 'pin must be 5 digits'),
  currentPin: z
    .string()
    .trim()
    .regex(/^\d{5}$/, 'currentPin must be 5 digits')
    .optional(),
});

export const RESERVED_DISPLAY_NAMES: ReadonlySet<string> = new Set([
  'admin',
  'support',
  'livdot',
  'host',
  'vendor',
  'system',
  'null',
  'undefined',
  'me',
  'settings',
  'viewer',
  'auth',
  'events',
  'streaming',
  'vendors',
  'marketplace',
  'users',
]);

/** Handle constraints — see viewer-settings.md §3.1. */
export const DisplayNameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3, 'Display name must be at least 3 characters')
  .max(30, 'Display name must be at most 30 characters')
  .regex(/^[a-z0-9_]+$/, 'Display name may only contain lowercase letters, digits, and underscores')
  .refine((v) => !RESERVED_DISPLAY_NAMES.has(v), {
    message: 'This display name is reserved',
  });

export const UpdateViewerProfileRequestSchema = z
  .object({
    displayName: DisplayNameSchema,
  })
  .strict();

export const UpdateViewerCurrencyRequestSchema = z
  .object({
    preferredCurrency: z
      .string()
      .trim()
      .toUpperCase()
      .length(3)
      .regex(/^[A-Z]{3}$/),
  })
  .strict();
const USER_CATEGORY = z.enum([
  NotificationCategory.LIVE_ALERTS,
  NotificationCategory.RECOMMENDATIONS,
  NotificationCategory.EVENT_REMINDERS,
]);

export const NotificationPreferenceItemSchema = z.object({
  category: USER_CATEGORY,
  enabled: z.boolean(),
});

export const UpdateNotificationPreferencesRequestSchema = z
  .object({
    preferences: z
      .array(NotificationPreferenceItemSchema)
      .min(1)
      .max(10)
      .refine((items) => new Set(items.map((i) => i.category)).size === items.length, {
        message: 'Duplicate category in preferences',
      }),
  })
  .strict();

export const SendFeedbackRequestSchema = z
  .object({
    category: z.enum(FeedbackCategory),
    message: z.string().trim().min(10).max(2000),
  })
  .strict();

export const ViewerMeResponseSchema = z.object({
  id: z.uuid(),
  email: z.email(),
  emailVerified: z.boolean(),
  displayName: z.string().nullable(),
  avatarUrl: z.url().nullable(),
  roles: z.number().int(),
  preferredCurrency: z.string(),
  notificationsEnabledCount: z.number().int(),
  hasPushDevice: z.boolean(),
});

export const NotificationPreferencesResponseSchema = z.object({
  preferences: z.record(USER_CATEGORY, z.boolean()),
});

export const SendFeedbackResponseSchema = z.object({
  id: z.uuid(),
});
